import { Suspense } from 'react'
import { Route, Routes } from 'react-router-dom'

import { AutoEnrollmentPage } from '@/AutoEnrollment/ui/pages/AutoEnrollmentPage'
import { CashbackAutoEnrollmentPage } from '@/AutoEnrollment/ui/pages/CashbackAutoEnrollmentPage'
import { RedirectPage } from '@/AutoEnrollment/ui/pages/RedirectPage'
import { AccountLayout } from '@/Account/ui/pages'
import { SignUpPage } from '@/AutoEnrollment/ui/pages/SignUpPage'
import { ChangeBankAccountPage } from '@/Contract/ui/pages/ChangeBankAccountPage'
import { CheckStatusOpenPage } from '@/Contract/ui/pages/CheckStatusOpenPage'
import { ContractBillingDataPage } from '@/Contract/ui/pages/ContractBillingDataPage'
import { ContractNewProductsPage } from '@/Contract/ui/pages/ContractNewProductsPage'
import { ContractProductsDataPage } from '@/Contract/ui/pages/ContractProductsDataPage'
import { HomePage } from '@/Home/ui/pages/HomePage'
import { InvoicesPage } from '@/Invoices/ui/pages/InvoicesPage'
import { AppLayout } from '@/Shared/ui/layout'
import { Loader } from '@/Shared/ui/loader'
import { navigationRoutes, openNavigationRoutes } from '@/Shared/ui/routers'
import { MyStorePage } from '@/Store/ui/pages/MyStorePage'
import { StoreContactsPage } from '@/Store/ui/pages/StoreContactsPage'
import { StoreStickersPage } from '@/Store/ui/pages/StoreStickersPage'
import { TpvsPage } from '@/Store/ui/pages/TpvsPage'
import { TransactionsPage } from '@/Transactions/ui/pages/TransactionsPage'
import { UserType } from '@/User/domain'
import { useUserData } from '@/User/ui/context'
import { ConfigurationPage } from '@/User/ui/pages/ConfigurationPage'
import { DeleteAccountSuccessPage } from '@/User/ui/pages/DeleteAccountSuccessPage'
import { AuthOtp, LoginForm, PasswordRecoveryForm, PasswordResetForm, StepOne, StepTwo } from '@/Account/ui/organisms'
import { PasswordRecoveryEmailSuccess, PasswordRecoveryPasswordChangeError, PasswordRecoveryPasswordChangeSuccess, RegisterEmailSuccess } from '@/Account/ui/molecules'
import { StepThree } from '@/Account/ui/organisms/StepThree'
import { AccessRolesPage } from '@/AccessRoles/ui/pages/AccesRolesPage'
import { NewTicketPage, MyTicketsPage, TicketDetailPage, ReopenTicketPage } from '@/HelpCenter/ui/pages'
import { TpvsExcelPage } from '@/Store/ui/pages/TpvsExcelPage'

const ClientRouter = (): JSX.Element => {
  const { userType } = useUserData()

  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        <Route element={<AppLayout />}>
          <Route path={navigationRoutes.home} element={<HomePage />} />
          <Route path={navigationRoutes.configuration} element={<ConfigurationPage />} />
          <Route path={navigationRoutes.invoices} element={<InvoicesPage />} />
          <Route path={navigationRoutes.transactions} element={<TransactionsPage />} />
          <Route path={navigationRoutes.storeData} element={<MyStorePage />} />
          <Route path={navigationRoutes.storeContacts} element={<StoreContactsPage />} />
          <Route path={navigationRoutes.storeStickers} element={<StoreStickersPage />} />
          <Route path={navigationRoutes.tpvsExcelUpload} element={<TpvsExcelPage />} />
          <Route path={navigationRoutes.accessRoles} element={<AccessRolesPage />} />
          <Route path={navigationRoutes.newTicket} element={<NewTicketPage />} />
          <Route path={navigationRoutes.myTickets} element={<MyTicketsPage />} />
          <Route path={navigationRoutes.ticketDetail} element={<TicketDetailPage />} />
          <Route path={navigationRoutes.reopenTicket} element={<ReopenTicketPage />} />

          <Route
            path={navigationRoutes.contractsBillingData}
            element={<ContractBillingDataPage />}
          />
          <Route
            path={navigationRoutes.changeBankAccount}
            element={<ChangeBankAccountPage />}
          />

          {userType === UserType.RESTAURANT && (
            <>
              <Route path={navigationRoutes.storeTPV} element={<TpvsPage />} />
              <Route
                path={navigationRoutes.changeBankAccount}
                element={<ChangeBankAccountPage />}
              />
              <Route
                path={navigationRoutes.contractsProductsData}
                element={<ContractProductsDataPage />}
              />
              <Route
                path={navigationRoutes.contractNewProducts}
                element={<ContractNewProductsPage />}
              />
            </>
          )}
          {userType === UserType.NURSERY && (
            <>
              <Route
                path={navigationRoutes.contractsProductsData}
                element={<ContractProductsDataPage />}
              />
              <Route
                path={navigationRoutes.contractsBillingData}
                element={<ContractBillingDataPage />}
              />
            </>
          )}
        </Route>

        {/* Login Layout */}
        <Route element={<AccountLayout />}>
          <Route path={openNavigationRoutes.login} element={<LoginForm />} />
          <Route path={openNavigationRoutes.otpAuth} element={<AuthOtp />} />
          <Route path={openNavigationRoutes.registerStepOne} element={<StepOne />} />
          <Route path={openNavigationRoutes.registerStepTwo} element={<StepTwo />} />
          <Route path={openNavigationRoutes.registerStepThree} element={<StepThree />} />
          <Route path={openNavigationRoutes.registerEmailSuccess} element={<RegisterEmailSuccess />} />
          <Route path={openNavigationRoutes.passwordRecoveryStep1} element={<PasswordRecoveryForm />} />
          <Route path={openNavigationRoutes.passwordRecoveryStep2} element={<PasswordResetForm />} />
          <Route path={openNavigationRoutes.passwordRecoveryEmailSuccess} element={<PasswordRecoveryEmailSuccess />} />
          <Route path={openNavigationRoutes.passwordRecoveryPasswordChangeSuccess} element={<PasswordRecoveryPasswordChangeSuccess />} />
          <Route path={openNavigationRoutes.passwordRecoveryPasswordChangeError} element={<PasswordRecoveryPasswordChangeError />} />

          {/* Old <SimpleAppLayout /> Pages*/}
          <Route path={openNavigationRoutes.logaltyCheckStatus} element={<CheckStatusOpenPage />} />
          <Route path={openNavigationRoutes.accountDeleted} element={<DeleteAccountSuccessPage />} />
          {/*
            This following (openNavigationRoutes.otp) route seems to be unusefull but,
            there is a test with this route here:
            src\Edenred.Merchants\react-app\tests\e2e\spec\signUp.spec.ts
          */}
          <Route path={openNavigationRoutes.otp} element={<AuthOtp />} />

          <Route path={openNavigationRoutes.signUp} element={<SignUpPage />} />
          <Route path={navigationRoutes.autoEnrollment} element={<AutoEnrollmentPage />} />
          <Route path={navigationRoutes.cashbackAutoEnrollment} element={<CashbackAutoEnrollmentPage />} />

        </Route>

        {/* No Layout (Empty page for redirects) */}
        <Route path="/redirect" element={<RedirectPage />} />
      </Routes>
    </Suspense>
  )
}

export { ClientRouter }
